import React, { useState, useEffect } from 'react';
import { FaSearch, FaSync, FaDownload, FaEye, FaMagic } from 'react-icons/fa';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

function getSpocAuthHeaders() {
  const token = localStorage.getItem('gogig_spoc_access');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };
}

/**
 * Company Monthly Reports for SPOC dashboard.
 * Same data shape and UI as Admin Company Reports; uses /api/spoc/* endpoints (scoped to SPOC's companies).
 */
function SPOCCompanyReports() {
  const currentYear = new Date().getFullYear();
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [year, setYear] = useState(currentYear);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatingMonth, setGeneratingMonth] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [manpowerForm, setManpowerForm] = useState({ total_manpower: '', effective_from_month: 1, effective_from_year: currentYear });
  const [savingManpower, setSavingManpower] = useState(false);

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const fetchCompanies = async () => {
    try {
      const response = await fetch(`${API_URL}/api/spoc/companies`, { headers: getSpocAuthHeaders() });
      const data = await response.json();
      setCompanies(Array.isArray(data) ? data : (data.companies || []));
    } catch (err) {
      console.error('Error fetching companies:', err);
    }
  };

  const fetchCompanyReports = async (companyId, yearOverride = year) => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/spoc/company-monthly-reports?company_id=${companyId}&year=${yearOverride}`,
        { headers: getSpocAuthHeaders() }
      );
      const data = await response.json();
      if (data.success) {
        setReports(data.reports || []);
        setSelectedCompany(data.company);
      } else {
        alert(data.message || 'Failed to fetch reports');
      }
    } catch (err) {
      console.error('Error fetching reports:', err);
      alert('Error fetching reports: ' + err.message);
    }
    setLoading(false);
  };

  const regenerateMonth = async (month) => {
    if (!selectedCompany) return;
    setGeneratingMonth(month);
    try {
      const response = await fetch(`${API_URL}/api/spoc/monthly-report/regenerate`, {
        method: 'POST',
        headers: getSpocAuthHeaders(),
        body: JSON.stringify({ company_id: selectedCompany.id, month, year })
      });
      const data = await response.json();
      if (data.success) {
        await fetchCompanyReports(selectedCompany.id);
      } else {
        alert(data.message || 'Failed to generate month report');
      }
    } catch (err) {
      alert('Error generating month report: ' + err.message);
    } finally {
      setGeneratingMonth(null);
    }
  };

  const regenerateYear = async () => {
    if (!selectedCompany) return;
    if (!window.confirm(`Rebuild all ${year} monthly reports for ${selectedCompany.company_name}? This may take a minute.`)) {
      return;
    }
    setGenerating(true);
    try {
      const response = await fetch(`${API_URL}/api/spoc/monthly-report/regenerate-year`, {
        method: 'POST',
        headers: getSpocAuthHeaders(),
        body: JSON.stringify({ company_id: selectedCompany.id, year })
      });
      const data = await response.json();
      if (data.success) {
        alert(data.message || 'Year reports generated');
        await fetchCompanyReports(selectedCompany.id);
      } else {
        alert(data.message || 'Failed to generate year reports');
      }
    } catch (err) {
      alert('Error generating year reports: ' + err.message);
    } finally {
      setGenerating(false);
    }
  };

  const saveManpower = async () => {
    if (!selectedCompany) return;
    setSavingManpower(true);
    try {
      const response = await fetch(
        `${API_URL}/api/spoc/companies/${selectedCompany.id}/manpower-configuration`,
        {
          method: 'PATCH',
          headers: getSpocAuthHeaders(),
          body: JSON.stringify({
            total_manpower: parseInt(manpowerForm.total_manpower, 10),
            effective_from_month: parseInt(manpowerForm.effective_from_month, 10),
            effective_from_year: parseInt(manpowerForm.effective_from_year, 10)
          })
        }
      );
      const data = await response.json();
      if (data.success) {
        fetchCompanyReports(selectedCompany.id);
      } else {
        alert(data.message || 'Failed to update manpower');
      }
    } catch (err) {
      alert('Error updating manpower: ' + err.message);
    } finally {
      setSavingManpower(false);
    }
  };

  const exportToCSV = (company, reportsList) => {
    const headers = [
      'Dimensions',
      ...reportsList.map(r => `${monthNames[r.month - 1].slice(0, 3)}'${String(year).slice(-2)}`)
    ];
    const rows = [
      ['Company', company.company_name],
      ['Establishment Code', company.establishment_code],
      ['Year', year],
      [],
      ['Contracts Generated', ...reportsList.map(r => r.naps_contracts_generated || 0)],
      ['Total Employee Payroll', ...reportsList.map(r => r.total_apprentice_payroll || 0)],
      ['eKYC Completed', ...reportsList.map(r => r.ekyc_percentage + '%' || '0%')],
      ['Total Manpower', ...reportsList.map(r => r.total_manpower || 0)],
      ['% of candidates as Employee', ...reportsList.map(r => r.apprentice_percentage + '%' || '0%')],
      ['Stipend/Salary paid by Company', ...reportsList.map(r => parseFloat(r.naps_stipend_paid || 0).toFixed(2))],
      ['DBT paid to candidates', ...reportsList.map(r => parseFloat(r.naps_dbt_paid || 0).toFixed(2))],
      ['Opportunity Loss', ...reportsList.map(r => parseFloat(r.opportunity_loss || 0).toFixed(2))],
      ['Terminations', ...reportsList.map(r => r.naps_terminations || 0)],
      ['Assessments Due', ...reportsList.map(r => r.naps_assessments_due || 0)]
    ];
    const csvContent = rows.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${company.company_name}_monthly_report_${year}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  const filteredCompanies = companies.filter(c =>
    (c.company_name && c.company_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (c.establishment_code && c.establishment_code.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ fontSize: 28, fontWeight: 800, color: '#22c55e', marginBottom: 24 }}>
        Company Monthly Reports
      </h2>
      <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        <div style={{
          width: 280, minWidth: 280, background: 'white', borderRadius: 12, padding: 16,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)', maxHeight: 600, overflowY: 'auto', flexShrink: 0
        }}>
          <div style={{ marginBottom: 16 }}>
            <div style={{ position: 'relative' }}>
              <FaSearch style={{ position: 'absolute', left: 12, top: 12, color: '#9ca3af' }} />
              <input
                type="text"
                placeholder="Search companies..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ width: '100%', padding: '10px 10px 10px 36px', borderRadius: 8, border: '2px solid #e5e7eb', fontSize: 14 }}
              />
            </div>
          </div>
          <div style={{ fontSize: 12, color: '#666', marginBottom: 12 }}>{filteredCompanies.length} companies</div>
          {filteredCompanies.map(company => (
            <div
              key={company.id}
              onClick={() => fetchCompanyReports(company.id)}
              style={{
                padding: 12, marginBottom: 8, borderRadius: 8,
                background: selectedCompany?.id === company.id ? '#dcfce7' : '#f9fafb',
                cursor: 'pointer',
                border: selectedCompany?.id === company.id ? '2px solid #22c55e' : '2px solid transparent',
                transition: 'all 0.2s'
              }}
            >
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>{company.company_name}</div>
              <div style={{ fontSize: 12, color: '#666' }}>{company.establishment_code || 'No Code'}</div>
            </div>
          ))}
        </div>
        <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
          {!selectedCompany ? (
            <div style={{ background: 'white', borderRadius: 12, padding: 48, textAlign: 'center', color: '#9ca3af', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <FaEye size={48} style={{ marginBottom: 16, opacity: 0.3 }} />
              <p>Select a company to view their monthly reports</p>
            </div>
          ) : (
            <>
              <div style={{
                background: 'white', borderRadius: 12, padding: 16, marginBottom: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12
              }}>
                <div>
                  <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>{selectedCompany.company_name}</h3>
                  <p style={{ fontSize: 14, color: '#666' }}>{selectedCompany.establishment_code || 'No establishment code'}</p>
                </div>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', background: '#f8fafc', padding: 8, borderRadius: 8 }}>
                    <input type="number" placeholder="Total manpower" value={manpowerForm.total_manpower}
                      onChange={(e) => setManpowerForm({ ...manpowerForm, total_manpower: e.target.value })}
                      style={{ width: 140, padding: 8, border: '1px solid #e5e7eb', borderRadius: 6 }} />
                    <select value={manpowerForm.effective_from_month} onChange={(e) => setManpowerForm({ ...manpowerForm, effective_from_month: parseInt(e.target.value) })}
                      style={{ padding: 8, border: '1px solid #e5e7eb', borderRadius: 6 }}>
                      {monthNames.map((name, idx) => <option key={idx + 1} value={idx + 1}>{name.slice(0, 3)}</option>)}
                    </select>
                    <select value={manpowerForm.effective_from_year} onChange={(e) => setManpowerForm({ ...manpowerForm, effective_from_year: parseInt(e.target.value) })}
                      style={{ padding: 8, border: '1px solid #e5e7eb', borderRadius: 6 }}>
                      {[...Array(6)].map((_, i) => { const y = currentYear - i; return <option key={y} value={y}>{y}</option>; })}
                    </select>
                    <button onClick={saveManpower} disabled={savingManpower || !manpowerForm.total_manpower}
                      style={{ padding: '8px 12px', borderRadius: 6, border: 'none', background: '#22c55e', color: 'white', cursor: savingManpower ? 'not-allowed' : 'pointer', opacity: savingManpower || !manpowerForm.total_manpower ? 0.6 : 1 }}>
                      {savingManpower ? 'Saving...' : 'Save Manpower'}
                    </button>
                  </div>
                  <select value={year} onChange={(e) => { const y = parseInt(e.target.value, 10); setYear(y); fetchCompanyReports(selectedCompany.id, y); }}
                    style={{ padding: '8px 16px', borderRadius: 8, border: '2px solid #e5e7eb', fontSize: 14, fontWeight: 600 }}>
                    {[...Array(6)].map((_, i) => { const y = currentYear - i; return <option key={y} value={y}>{y}</option>; })}
                  </select>
                  <button
                    onClick={regenerateYear}
                    disabled={generating}
                    style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#7c3aed', color: 'white', cursor: generating ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 8, opacity: generating ? 0.6 : 1 }}
                  >
                    <FaMagic /> {generating ? 'Generating…' : `Generate ${year}`}
                  </button>
                  <button onClick={() => fetchCompanyReports(selectedCompany.id)}
                    style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#3b82f6', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <FaSync /> Refresh
                  </button>
                  <button onClick={() => exportToCSV(selectedCompany, reports)} disabled={reports.length === 0}
                    style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#10b981', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, opacity: reports.length === 0 ? 0.5 : 1 }}>
                    <FaDownload /> Export
                  </button>
                </div>
              </div>
              {loading ? (
                <div style={{ background: 'white', borderRadius: 12, padding: 48, textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>Loading reports...</div>
              ) : reports.length === 0 ? (
                <div style={{ background: 'white', borderRadius: 12, padding: 48, textAlign: 'center', color: '#9ca3af', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                  <p style={{ marginBottom: 16 }}>No reports available for {year}</p>
                  <button onClick={regenerateYear} disabled={generating}
                    style={{ padding: '10px 20px', borderRadius: 8, border: 'none', background: '#7c3aed', color: 'white', cursor: generating ? 'not-allowed' : 'pointer', fontWeight: 600 }}>
                    {generating ? 'Generating…' : `Generate ${year} reports`}
                  </button>
                </div>
              ) : (
                <div style={{ background: 'white', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflowX: 'auto', maxWidth: '100%' }}>
                  <table style={{ width: 'max-content', minWidth: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: 'linear-gradient(90deg, #22c55e 0%, #2ad2c9 100%)' }}>
                        <th style={{ padding: 8, textAlign: 'left', color: 'white', fontWeight: 700, position: 'sticky', left: 0, background: '#22c55e', zIndex: 10, minWidth: 180, maxWidth: 180 }}>Dimensions</th>
                        {reports.map(report => (
                          <th key={report.month} style={{ padding: 8, textAlign: 'center', color: 'white', fontWeight: 700, minWidth: 80, maxWidth: 80, whiteSpace: 'nowrap' }}>
                            {monthNames[report.month - 1].slice(0, 3)}'{String(year).slice(-2)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr style={{ background: '#f9fafb' }}><td style={{ padding: 8, fontWeight: 600, position: 'sticky', left: 0, background: '#f9fafb', zIndex: 9, fontSize: 11 }}>Contracts Generated</td>{reports.map(r => <td key={r.month} style={{ padding: 8, textAlign: 'center', fontSize: 11 }}>{r.naps_contracts_generated || 0}</td>)}</tr>
                      <tr><td style={{ padding: 8, fontWeight: 600, position: 'sticky', left: 0, background: 'white', zIndex: 9, fontSize: 11 }}>Total Employee Payroll</td>{reports.map(r => <td key={r.month} style={{ padding: 8, textAlign: 'center', fontSize: 11 }}>{r.total_apprentice_payroll || 0}</td>)}</tr>
                      <tr style={{ background: '#f9fafb' }}><td style={{ padding: 8, fontWeight: 600, position: 'sticky', left: 0, background: '#f9fafb', zIndex: 9, fontSize: 11 }}>eKYC Completed</td>{reports.map(r => <td key={r.month} style={{ padding: 8, textAlign: 'center', fontSize: 11 }}>{r.ekyc_percentage}%</td>)}</tr>
                      <tr><td style={{ padding: 8, fontWeight: 600, position: 'sticky', left: 0, background: 'white', zIndex: 9, fontSize: 11 }}>Total Manpower</td>{reports.map(r => <td key={r.month} style={{ padding: 8, textAlign: 'center', fontSize: 11 }}>{r.total_manpower || 0}</td>)}</tr>
                      <tr style={{ background: '#f9fafb' }}><td style={{ padding: 8, fontWeight: 600, position: 'sticky', left: 0, background: '#f9fafb', zIndex: 9, fontSize: 11 }}>% as Employee</td>{reports.map(r => <td key={r.month} style={{ padding: 8, textAlign: 'center', color: parseFloat(r.apprentice_percentage) >= 15 ? '#10b981' : '#f59e0b', fontSize: 11 }}>{r.apprentice_percentage}%</td>)}</tr>
                      <tr><td style={{ padding: 8, fontWeight: 600, position: 'sticky', left: 0, background: 'white', zIndex: 9, fontSize: 11 }}>Stipend/Salary Paid</td>{reports.map(r => <td key={r.month} style={{ padding: 8, textAlign: 'right', fontSize: 10, whiteSpace: 'nowrap' }}>₹{parseFloat(r.naps_stipend_paid || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>)}</tr>
                      <tr style={{ background: '#f9fafb' }}><td style={{ padding: 8, fontWeight: 600, position: 'sticky', left: 0, background: '#f9fafb', zIndex: 9, fontSize: 11 }}>DBT Paid</td>{reports.map(r => <td key={r.month} style={{ padding: 8, textAlign: 'right', fontSize: 10, whiteSpace: 'nowrap' }}>₹{parseFloat(r.naps_dbt_paid || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>)}</tr>
                      <tr><td style={{ padding: 8, fontWeight: 600, position: 'sticky', left: 0, background: 'white', zIndex: 9, fontSize: 11 }}>Opportunity Loss</td>{reports.map(r => <td key={r.month} style={{ padding: 8, textAlign: 'right', color: '#dc2626', fontSize: 10, whiteSpace: 'nowrap' }}>₹{parseFloat(r.opportunity_loss || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>)}</tr>
                      <tr style={{ background: '#f9fafb' }}><td style={{ padding: 8, fontWeight: 600, position: 'sticky', left: 0, background: '#f9fafb', zIndex: 9, fontSize: 11 }}>Terminations</td>{reports.map(r => <td key={r.month} style={{ padding: 8, textAlign: 'center', fontSize: 11 }}>{r.naps_terminations || 0}</td>)}</tr>
                      <tr><td style={{ padding: 8, fontWeight: 600, position: 'sticky', left: 0, background: 'white', zIndex: 9, fontSize: 11 }}>Assessments Due</td>{reports.map(r => <td key={r.month} style={{ padding: 8, textAlign: 'center', fontSize: 11 }}>{r.naps_assessments_due || 0}</td>)}</tr>
                      <tr style={{ background: '#eef2ff' }}>
                        <td style={{ padding: 8, fontWeight: 700, position: 'sticky', left: 0, background: '#eef2ff', zIndex: 9, fontSize: 11 }}>Rebuild month</td>
                        {reports.map(r => (
                          <td key={r.month} style={{ padding: 6, textAlign: 'center' }}>
                            <button
                              type="button"
                              onClick={() => regenerateMonth(r.month)}
                              disabled={generating || generatingMonth === r.month}
                              style={{
                                padding: '4px 8px', fontSize: 10, borderRadius: 6, border: '1px solid #7c3aed',
                                background: generatingMonth === r.month ? '#ede9fe' : 'white', color: '#7c3aed',
                                cursor: generating || generatingMonth === r.month ? 'not-allowed' : 'pointer', fontWeight: 600
                              }}
                            >
                              {generatingMonth === r.month ? '…' : 'Generate'}
                            </button>
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default SPOCCompanyReports;
